import { useEffect, useRef, useState } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  buildAgentStreamUrl,
  subscribeAgentSseStream,
} from '@/lib/agent-sse-stream'

export type AgentLiveStreamPanelProps = {
  agentId: string
  organizationId?: string | null
  /** When set, used instead of building URL from agentId */
  streamUrl?: string
  /** Shown while waiting for first SSE line (not a new dispatch) */
  connectingHint?: string
  className?: string
  terminalClassName?: string
}

/**
 * Authenticated SSE terminal for Cursor cloud agent status polling.
 * Use in a dialog or inbox reader - never window.open (no Authorization header).
 */
export function AgentLiveStreamPanel({
  agentId,
  organizationId,
  streamUrl: streamUrlProp,
  connectingHint = 'Connecting to agent stream...',
  className,
  terminalClassName,
}: AgentLiveStreamPanelProps) {
  const [lines, setLines] = useState<string[]>([])
  const [streamDone, setStreamDone] = useState(false)
  const [connecting, setConnecting] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [httpError, setHttpError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const streamUrl =
    streamUrlProp?.trim() || buildAgentStreamUrl(agentId, organizationId)

  useEffect(() => {
    const ac = new AbortController()
    setLines([])
    setStreamDone(false)
    setConnecting(true)
    setAuthError(null)
    setHttpError(null)

    void subscribeAgentSseStream(
      streamUrl,
      {
        onStatusJSON: (data) => {
          const text = typeof data.text === 'string' ? data.text : null
          if (text) {
            setConnecting(false)
            setLines((prev) => [...prev, text])
            requestAnimationFrame(() => {
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight
              }
            })
          }
        },
        onAuthRequired: ({ message }) => {
          setConnecting(false)
          setAuthError(message)
          setStreamDone(true)
        },
        onHttpError: ({ message }) => {
          setConnecting(false)
          setHttpError(message)
        },
        onDone: () => {
          setConnecting(false)
          setStreamDone(true)
        },
      },
      ac.signal
    )

    return () => ac.abort()
  }, [streamUrl])

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {authError ? (
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4"
          data-testid={testIds.inbox.agentStreamAuthError}
          role="alert"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-medium text-foreground">Sign in required</p>
              <p className="text-xs leading-relaxed text-muted-foreground">{authError}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Refresh and sign in
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {httpError && !authError ? (
        <div
          className="rounded-md border border-red-500/20 bg-red-500/5 p-3"
          data-testid={testIds.inbox.agentStreamHttpError}
          role="alert"
        >
          <p className="text-xs text-red-400">Could not open agent stream: {httpError}</p>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className={cn(
          'h-64 overflow-y-auto rounded-md border border-border/40 bg-black/80 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-green-300',
          terminalClassName
        )}
        data-testid={testIds.inbox.agentStreamTerminal}
      >
        {connecting && lines.length === 0 && !authError && (
          <span className="animate-pulse text-muted-foreground">{connectingHint}</span>
        )}
        {lines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
        {streamDone && !authError && (
          <div className="mt-2 text-muted-foreground">-- stream ended --</div>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Agent <code className="rounded bg-muted px-1">{agentId}</code>
        {organizationId ? (
          <>
            {' '}
            - workspace{' '}
            <code className="rounded bg-muted px-1">{organizationId.slice(0, 8)}...</code>
          </>
        ) : null}
      </p>
    </div>
  )
}
